import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Dropdown, Table } from 'react-bootstrap';

import CogIcon from 'ui-kit/icons/cog-icon/cog-icon';
import Select from 'ui-kit/select/select';

import './style.css';

const DataGridFilter = ({ data, filterCol, onFilterChange, reset }) => {
    if (!data) return null;

    const values = [...new Set(data.map((row) => row[filterCol.field]))];

    const options = values.map((value) => ({
        key: `data-grid-${filterCol.field}-option-${value}`,
        value,
        label: value
    }));

    const handleChange = (option) => {
        console.log('option', option);
        onFilterChange({ field: filterCol.field, ...option });
    };

    return (
        <div className="data-grid-filter ml-1">
            <Select
                placeholder={filterCol.label}
                options={options}
                label={filterCol.label}
                onChange={handleChange}
                reset={reset}
            />
        </div>
    );
};

const DataGridRow = ({ data, gridId, columns }) => {
    const key = columns.find((col) => col.key);
    const actions = columns.find((col) => col.field === 'actions');
    const displayColumns = columns.filter((col) => col.field !== 'actions' && !col.hide);

    return (
        <tr key={data[key.field]}>
            {displayColumns &&
                displayColumns.map((col) => (
                    <td key={`${gridId}-table-header-col-${col.field}`}>
                        {col.cellRenderer ? col.cellRenderer(data[col.field], data) : data[col.field]}
                    </td>
                ))}
            {actions && <td className="data-grid-row-actions">{actions.cellRenderer(data)}</td>}
        </tr>
    );
};

export const SORT_DIRECTION = {
    ASC: 'asc',
    DESC: 'desc',
    NONE: 'none'
};
const DataGrid = ({ id, className, columns, data, defaultSortCol, filters }) => {
    const [lastReset, setlastReset] = useState(undefined);
    const [sortedData, setSortedData] = useState(undefined);
    const [sortDirection, setSortDirection] = useState(SORT_DIRECTION.NONE);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const classes = classNames('data-grid', className);

    const defaultSort = (a, b) => {
        if (sortDirection === SORT_DIRECTION.ASC) {
            if (a < b) return -1;
            return a > b ? 1 : 0;
        }

        if (a < b) return 1;
        return a > b ? -1 : 0;
    };

    const handleSort = ({ col, dataToSort }) => {
        const { sort } = col;
        const nextSortedData = [...dataToSort];
        nextSortedData.sort(
            typeof sort === 'function'
                ? (a, b) =>
                      sort(
                          a[col.field],
                          b[col.field],
                          sortDirection === SORT_DIRECTION.DESC ? SORT_DIRECTION.ASC : SORT_DIRECTION.DESC
                      )
                : (a, b) =>
                      defaultSort(
                          a[col.field],
                          b[col.field],
                          sortDirection === SORT_DIRECTION.DESC ? SORT_DIRECTION.ASC : SORT_DIRECTION.DESC
                      )
        );
        setSortedData(nextSortedData);
        setSortDirection(sortDirection === SORT_DIRECTION.DESC ? SORT_DIRECTION.ASC : SORT_DIRECTION.DESC);
    };

    const handleAddFilter = ({ field, value }) => {
        console.log('field', field);
        console.log('value', value);
        if (!selectedFilters.find((el) => el.field === field)) {
            setSelectedFilters([...selectedFilters, { field, value }]);
        } else {
            const nextSelectedFilters = [...selectedFilters.filter((el) => el.field !== field), { field, value }];
            setSelectedFilters(nextSelectedFilters);
        }
    };

    const handleResetFilters = () => {
        setlastReset(new Date());
        setSelectedFilters([]);
    };

    const handleExportAsCSVClick = () => {
        const exportableCols = columns.filter((col) => col.exportable);

        const rows = [
            [...exportableCols.map((col) => col.field)],
            ...data.map((row) => exportableCols.map((col) => row[col.field]))
        ];

        const csvContent = `data:text/csv;charset=utf-8,${rows.map((e) => e.join(',')).join('\n')}`;
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `${id}-${Date.now()}.csv`);

        document.body.appendChild(link); // Required for FF

        link.click(); // This will download the data file named "my_data.csv".
    };

    const renderFilters = () => {
        if (!data || !filters) return null;

        return (
            <div className="data-grid-filters d-flex justify-content-end mb-3">
                <div className="d-flex flex-column">
                    <h5 className="align-self-start">Filters</h5>
                    <div className="d-flex align-items-start">
                        {filters.map((filterCol) => (
                            <DataGridFilter
                                data={data}
                                filterCol={filterCol}
                                onFilterChange={handleAddFilter}
                                reset={lastReset}
                            />
                        ))}
                        <Dropdown className="ml-1">
                            <Dropdown.Toggle>
                                <CogIcon style={{ height: '1.5rem', width: '1.5rem' }} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={handleResetFilters}>Reset</Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={handleExportAsCSVClick}>Export CSV</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
            </div>
        );
    };

    const isFiltered = ({ row }) => {
        if (!selectedFilters || selectedFilters.length === 0) return false;
        const filtersResult = selectedFilters.map(({ field, value }) => (row[field] === value ? 1 : 0));
        return filtersResult?.reduce((sum, x) => sum + x) !== selectedFilters.length;
    };

    //  subsequent filter/sort direction updates
    useEffect(() => {
        if (!data || !selectedFilters) return;

        const filtered = data.filter((row) => !isFiltered({ row }));
        if (filtered) {
            setSortedData(filtered);
        }
    }, [selectedFilters]);

    //  initial sort
    useEffect(() => {
        if (sortedData && sortedData.length > 0 && defaultSortCol && sortDirection === SORT_DIRECTION.NONE) {
            handleSort({ col: defaultSortCol, dataToSort: sortedData });
        }
    }, [sortedData]);

    //  initial data set
    useEffect(() => {
        if (data) setSortedData(data);
    }, [data]);

    return (
        <div id={id} className={classes}>
            {filters && renderFilters()}
            <Table striped bordered hover>
                <thead>
                    <tr>
                        {columns &&
                            columns
                                .filter((col) => !col.hide)
                                .map((col) => (
                                    <th key={`data-grid-${id || new Date().getMilliseconds()}-${col.field}`}>
                                        {col && col.sort && (
                                            <span
                                                onClick={() => handleSort({ col, dataToSort: sortedData })}
                                                role="button"
                                                tabIndex={0}
                                                className="sortable"
                                            >
                                                {col.label}
                                            </span>
                                        )}
                                        {col && !col.sort && col.label}
                                    </th>
                                ))}
                    </tr>
                </thead>
                <tbody>
                    {sortedData &&
                        sortedData.map((d) => (
                            <DataGridRow key={`data-grid-${id}-${d.id}`} data={d} gridId={id} columns={columns} />
                        ))}
                </tbody>
            </Table>
        </div>
    );
};

export default DataGrid;
